import { datum, type Datum } from './datum';

/**
 * WebGL renderer classification rules.
 *
 * The single most common automation tell is a software rasterizer: a real
 * browser runs on GPU-backed ANGLE, while headless Chromium and VMs fall back
 * to SwiftShader or llvmpipe. This table classifies a renderer string into
 * (a) whether it is a real GPU or a software rasterizer, and (b) what OS the
 * graphics stack implies, for coherence checks against the claimed UA.
 */

export type GlClass = 'gpu' | 'software' | 'virtualized';

export interface GlVerdict {
  class: GlClass;
  /** 'macOS' | 'Windows' | 'Linux/VM' | 'mobile' | 'unknown' */
  impliedOs: string;
  label: string;
}

interface Rule {
  test: RegExp;
  verdict: GlVerdict;
  source: string;
  confidence: Datum<GlVerdict>['confidence'];
}

/**
 * Ordered rules: the first match wins, so specific patterns (SwiftShader) are
 * listed before broad ones (nvidia/amd/intel).
 */
const RULES: Rule[] = [
  {
    test: /swiftshader/i,
    verdict: { class: 'software', impliedOs: 'Linux/VM', label: 'SwiftShader (software rasterizer)' },
    source: 'https://developer.chrome.org/blog/headless/',
    confidence: 'high',
  },
  {
    test: /llvmpipe|softpipe/i,
    verdict: { class: 'software', impliedOs: 'Linux/VM', label: 'Mesa software rasterizer' },
    source: 'https://docs.mesa3d.org/drivers/llvmpipe.html',
    confidence: 'high',
  },
  {
    test: /microsoft basic render|warp/i,
    verdict: { class: 'software', impliedOs: 'Windows', label: 'WARP software rasterizer' },
    source: 'https://learn.microsoft.com/en-us/windows/win32/directwrite/dxgi-factory',
    confidence: 'medium',
  },
  {
    test: /apple/i,
    verdict: { class: 'gpu', impliedOs: 'macOS', label: 'Apple GPU (Metal/ANGLE)' },
    source: 'https://developer.apple.com/metal/',
    confidence: 'high',
  },
  {
    test: /nvidia/i,
    verdict: { class: 'gpu', impliedOs: 'Windows/Linux', label: 'NVIDIA GPU' },
    source: 'https://developer.nvidia.com/',
    confidence: 'high',
  },
  {
    test: /(amd|ati|radeon)/i,
    verdict: { class: 'gpu', impliedOs: 'Windows/Linux', label: 'AMD GPU' },
    source: 'https://gpuopen.com/',
    confidence: 'high',
  },
  {
    test: /intel/i,
    verdict: { class: 'gpu', impliedOs: 'Windows/Linux', label: 'Intel GPU' },
    source: 'https://www.intel.com/content/www/us/en/products/details/processors/graphics.html',
    confidence: 'high',
  },
  {
    test: /(adreno|mali|powervr)/i,
    verdict: { class: 'gpu', impliedOs: 'mobile', label: 'Mobile GPU' },
    source: 'https://www.khronos.org/webgl/extension/EXT_disjoint_timer_query/',
    confidence: 'medium',
  },
  {
    test: /mesa/i,
    verdict: { class: 'gpu', impliedOs: 'Linux/VM', label: 'Mesa (possibly virtualized)' },
    source: 'https://www.mesa3d.org/',
    confidence: 'medium',
  },
  {
    test: /vmware|virtualbox|qemu|virtio|hyper-v|xen|parallels/i,
    verdict: { class: 'virtualized', impliedOs: 'Linux/VM', label: 'Virtualized graphics adapter' },
    source: 'https://www.kubevirt.io/',
    confidence: 'medium',
  },
];

/** Classifies a WebGL renderer string. Returns null when nothing matches. */
export function classifyGl(renderer: string): Datum<GlVerdict> | null {
  for (const rule of RULES) {
    if (rule.test.test(renderer)) {
      return datum(rule.verdict, rule.source, rule.confidence);
    }
  }
  return null;
}

export const GL_RULE_SOURCE_COUNT = RULES.length;
